import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from 'react';
import { ChevronRight, Search } from '@mui/icons-material';
import { OverflowTooltip } from 'components/ui';
import { getSearchPlaceholder } from './SuperSearch.presentation';
import {
  AssistiveText,
  ContextButton,
  ContextCurrent,
  ContextRow,
  ContextSeparator,
  GhostPrefix,
  GhostText,
  SearchHeader as SearchHeaderRoot,
  SearchInput,
  SearchInputRow,
  SearchTextField,
} from './SuperSearch.styles';
import { SUPER_SEARCH_TEST_IDS } from './SuperSearch.testIds';
import type { SuperSearchContext, SuperSearchDisplayItem, SuperSearchPendingPage } from './SuperSearch.types';

interface SuperSearchHeaderProps {
  context: SuperSearchContext;
  ghostCompletion: string;
  isContextDimmed: boolean;
  onBlur: () => void;
  onContextClick: (scope: 'company' | 'firm') => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLInputElement>) => void;
  onQueryChange: (query: string) => void;
  open: boolean;
  pendingPage?: SuperSearchPendingPage;
  query: string;
  searchInputRef: RefObject<HTMLInputElement | null>;
  selectedItem?: SuperSearchDisplayItem;
}

const SuperSearchHeader = ({
  context,
  ghostCompletion,
  isContextDimmed,
  onBlur,
  onContextClick,
  onKeyDown,
  onQueryChange,
  open,
  pendingPage,
  query,
  searchInputRef,
  selectedItem,
}: SuperSearchHeaderProps) => {
  const searchPlaceholder = getSearchPlaceholder(context, pendingPage);

  return (
    <SearchHeaderRoot>
      <SearchInputRow>
        <Search aria-hidden />
        {(context.firm || context.company || pendingPage) && (
          <ContextRow isDimmed={isContextDimmed}>
            {context.firm && (
              <OverflowTooltip placement="top" title={context.firm.name}>
                {context.company || pendingPage ? (
                  <ContextButton
                    aria-label={`Search within ${context.firm.name} and clear the ${context.company ? 'company scope' : 'pending page'}`}
                    onClick={() => onContextClick('firm')}
                    tone="firm">
                    {context.firm.name}
                  </ContextButton>
                ) : (
                  <ContextCurrent tone="firm">{context.firm.name}</ContextCurrent>
                )}
              </OverflowTooltip>
            )}
            {context.company && (
              <>
                <ContextSeparator aria-hidden>
                  <ChevronRight />
                </ContextSeparator>
                <OverflowTooltip placement="top" title={context.company.name}>
                  {pendingPage ? (
                    <ContextButton
                      aria-label={`Search within ${context.company.name} and clear the pending page`}
                      onClick={() => onContextClick('company')}
                      tone="company">
                      {context.company.name}
                    </ContextButton>
                  ) : (
                    <ContextCurrent tone="company">{context.company.name}</ContextCurrent>
                  )}
                </OverflowTooltip>
              </>
            )}
            {pendingPage && (
              <>
                <ContextSeparator aria-hidden>
                  <ChevronRight />
                </ContextSeparator>
                <OverflowTooltip placement="top" title={pendingPage.label}>
                  <ContextCurrent tone="companyPage">{pendingPage.label}</ContextCurrent>
                </OverflowTooltip>
              </>
            )}
            <ContextSeparator aria-hidden>
              <ChevronRight />
            </ContextSeparator>
          </ContextRow>
        )}
        <SearchTextField>
          <SearchInput
            autoFocus
            inputRef={searchInputRef}
            inputProps={{
              'aria-activedescendant': selectedItem ? SUPER_SEARCH_TEST_IDS.result(selectedItem.id) : undefined,
              'aria-autocomplete': 'list',
              'aria-controls': SUPER_SEARCH_TEST_IDS.results,
              'aria-describedby': SUPER_SEARCH_TEST_IDS.instructions,
              'aria-expanded': open,
              'aria-label': 'Search firms, companies, documents, pages, and measurement dates',
              'data-testid': SUPER_SEARCH_TEST_IDS.input,
              role: 'combobox',
            }}
            onBlur={onBlur}
            onChange={event => onQueryChange(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            value={query}
          />
          {ghostCompletion && (
            <GhostText aria-hidden>
              <GhostPrefix>{query}</GhostPrefix>
              {ghostCompletion}
            </GhostText>
          )}
          <AssistiveText id={SUPER_SEARCH_TEST_IDS.instructions}>
            Type to search. Use the Up and Down Arrow keys to navigate results, Tab to set scope when available, Enter
            to navigate, Backspace on an empty search to remove scope, and Escape to close.
          </AssistiveText>
        </SearchTextField>
      </SearchInputRow>
    </SearchHeaderRoot>
  );
};

export default SuperSearchHeader;
